import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { 
  Shield, 
  User, 
  FileText, 
  Camera, 
  CheckCircle2, 
  Loader2,
  Upload,
  ArrowRight,
  ArrowLeft,
  AlertTriangle,
  X,
  Smartphone,
  Monitor,
  QrCode,
  RefreshCw,
  Info,
  Lock
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, title: 'Personal Info', icon: User },
  { id: 2, title: 'ID Document', icon: FileText },
  { id: 3, title: 'Selfies', icon: Camera },
  { id: 4, title: 'Review', icon: CheckCircle2 },
];

const DOCUMENT_TYPES = [
  { value: 'national_id', label: 'National ID Card' },
  { value: 'passport', label: 'Passport' },
  { value: 'drivers_license', label: "Driver's License" },
];

// Detect if on mobile device
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export function VerifyFlow() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savingProgress, setSavingProgress] = useState(false);
  const [error, setError] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [showContinueDialog, setShowContinueDialog] = useState(false);
  const [existingProgress, setExistingProgress] = useState(null);
  const [countryDocInfo, setCountryDocInfo] = useState(null);
  
  const [formData, setFormData] = useState({
    full_name: '',
    date_of_birth: '',
    document_type: 'national_id',
    document_number: '',
    id_front: null,
    id_front_preview: null,
    id_back: null,
    id_back_preview: null,
    selfie_front: null,
    selfie_front_preview: null,
    selfie_side: null,
    selfie_side_preview: null,
    consent_given: false
  });

  const fileInputFrontRef = useRef(null);
  const fileInputBackRef = useRef(null);
  const fileInputSelfieFrontRef = useRef(null);
  const fileInputSelfieSideRef = useRef(null);
  // Gallery upload refs (no capture attribute - allows choosing from gallery)
  const galleryInputFrontRef = useRef(null);
  const galleryInputBackRef = useRef(null);
  const galleryInputSelfieFrontRef = useRef(null);
  const galleryInputSelfieSideRef = useRef(null);
  const [deviceId] = useState(() => localStorage.getItem('verify_device_id') || Math.random().toString(36).substring(7));
  const [isDeviceLocked, setIsDeviceLocked] = useState(false);
  const [activeDevice, setActiveDevice] = useState(null);
  const [sessionTakenOver, setSessionTakenOver] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const sessionCheckIntervalRef = useRef(null);

  const deviceType = isMobile ? 'mobile' : 'desktop';
  const verificationUrl = typeof window !== 'undefined' 
    ? `${window.location.origin}/verify/${token}`
    : '';

  // Fetch country-specific document recommendations
  const fetchCountryDocInfo = useCallback(async (countryCode) => {
    if (!countryCode) return;
    try {
      const response = await axios.get(`${API_URL}/api/country-documents/${countryCode}`);
      setCountryDocInfo(response.data);
      
      // Set default document type based on country recommendation
      if (response.data.primary_document && !formData.document_type) {
        setFormData(prev => ({ ...prev, document_type: response.data.primary_document }));
      }
    } catch (error) {
      console.log('Could not fetch country doc info:', error);
    }
  }, [formData.document_type]);

  // Claim session - this device becomes the active one
  const claimSession = useCallback(async () => {
    if (!token) return false;
    
    try {
      const response = await axios.post(`${API_URL}/api/verify/${token}/claim-session`, {
        device_type: deviceType,
        device_id: deviceId
      });
      
      if (response.data.success) {
        setActiveDevice(deviceType); // WE are now active
        setIsDeviceLocked(false);
        setSessionTakenOver(false);
        setSessionReady(true);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to claim session:', err);
      if (err.response?.status === 400) {
        setIsSubmitted(true);
      }
      return false;
    }
  }, [token, deviceType, deviceId]);

  // Check if ANOTHER device has taken over our session
  const checkSessionStatus = useCallback(async () => {
    if (!token || isSubmitted || !sessionReady) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/verify/${token}/session-status?device_type=${deviceType}`);
      
      if (response.data.submitted) {
        setIsSubmitted(true);
        setSessionTakenOver(false);
        setShowQRDialog(false);  // Close QR modal when submitted
        return;
      }
      
      // Check if another device has taken over
      const currentActiveDevice = response.data.active_device;
      if (currentActiveDevice && currentActiveDevice !== deviceType) {
        // Another device is now active - we are blocked
        setSessionTakenOver(true);
        setActiveDevice(currentActiveDevice);
        setShowQRDialog(false);  // Close QR modal when session transferred
      } else {
        // We are still active
        setSessionTakenOver(false);
        setActiveDevice(deviceType);
      }
    } catch (err) {
      console.error('Failed to check session status:', err);
    }
  }, [token, deviceType, isSubmitted, sessionReady]);

  // Start session checking ONLY after we've claimed the session
  useEffect(() => {
    if (sessionReady && !isSubmitted) {
      // Start polling for other devices taking over
      sessionCheckIntervalRef.current = setInterval(checkSessionStatus, 2000);
      
      return () => {
        if (sessionCheckIntervalRef.current) {
          clearInterval(sessionCheckIntervalRef.current);
        }
      };
    }
  }, [sessionReady, isSubmitted, checkSessionStatus]);

  // Save progress to backend with device locking - includes image data for cross-device sync
  const saveProgress = useCallback(async (step, additionalData = {}) => {
    if (!token) return;
    
    try {
      setSavingProgress(true);
      
      // Build progress data including images for cross-device sync
      const progressData = {
        current_step: step,
        full_name: formData.full_name || null,
        date_of_birth: formData.date_of_birth || null,
        document_type: formData.document_type || null,
        document_number: formData.document_number || null,
        id_front_uploaded: !!formData.id_front_preview,
        id_back_uploaded: !!formData.id_back_preview,
        selfie_front_uploaded: !!formData.selfie_front_preview,
        selfie_side_uploaded: !!formData.selfie_side_preview,
        device_type: deviceType,
        device_id: deviceId,
        ...additionalData
      };
      
      // Include image data for cross-device sync (only when images exist)
      if (formData.id_front_preview) {
        progressData.id_front_base64 = formData.id_front_preview;
      }
      if (formData.id_back_preview) {
        progressData.id_back_base64 = formData.id_back_preview;
      }
      if (formData.selfie_front_preview) {
        progressData.selfie_front_base64 = formData.selfie_front_preview;
      }
      if (formData.selfie_side_preview) {
        progressData.selfie_side_base64 = formData.selfie_side_preview;
      }
      
      const response = await axios.put(`${API_URL}/api/verify/${token}/progress`, progressData);
      
      // Handle device lock response
      if (response.data.locked) {
        setIsDeviceLocked(true);
        setActiveDevice(response.data.active_device);
        toast.error(`Session is active on ${response.data.active_device}. Please continue there.`);
        return false;
      }
      
      if (response.data.active_device) {
        setActiveDevice(response.data.active_device);
      }
      
      return true;
    } catch (err) {
      console.error('Failed to save progress:', err);
      if (err.response?.status === 400 && err.response?.data?.detail?.includes('already submitted')) {
        setError('This verification has already been submitted.');
        setIsSubmitted(true);
      }
      return false;
    } finally {
      setSavingProgress(false);
    }
  }, [token, formData, deviceType, deviceId]);

  // Store device ID for consistency
  useEffect(() => {
    localStorage.setItem('verify_device_id', deviceId);
  }, [deviceId]);

  // Load invitation and check for existing progress
  useEffect(() => {
    const mobile = isMobileDevice();
    setIsMobile(mobile);
    
    const fetchInvitation = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/verify/${token}`);
        setInvitation(response.data);
        
        // Fetch country-specific document recommendations
        if (response.data.country) {
          fetchCountryDocInfo(response.data.country);
        }
        
        // Check if already submitted
        if (response.data.status === 'submitted') {
          setIsSubmitted(true);
          setLoading(false);
          return;
        }
        
        // Check for existing progress
        if (response.data.progress && response.data.progress.current_step > 1) {
          setExistingProgress(response.data.progress);
          setShowContinueDialog(true);
        }
        
        // Pre-fill from invitation or progress - including images
        const progress = response.data.progress;
        setFormData(prev => ({
          ...prev,
          full_name: progress?.full_name || response.data.candidate_name || '',
          date_of_birth: progress?.date_of_birth || '',
          document_type: progress?.document_type || 'national_id',
          document_number: progress?.document_number || '',
          // Restore images from progress (cross-device sync)
          id_front_preview: progress?.id_front_base64 || null,
          id_back_preview: progress?.id_back_base64 || null,
          selfie_front_preview: progress?.selfie_front_base64 || null,
          selfie_side_preview: progress?.selfie_side_base64 || null
        }));
        
        // Claim the session for this device - use local mobile check
        const currentDeviceType = mobile ? 'mobile' : 'desktop';
        try {
          const claimResponse = await axios.post(`${API_URL}/api/verify/${token}/claim-session`, {
            device_type: currentDeviceType,
            device_id: deviceId
          });
          
          if (claimResponse.data.success) {
            setActiveDevice(currentDeviceType);
            setIsDeviceLocked(false);
            setSessionTakenOver(false);
            setSessionReady(true);
          }
        } catch (claimErr) {
          console.error('Failed to claim session:', claimErr);
          if (claimErr.response?.status === 400) {
            setIsSubmitted(true);
          }
        }
        
      } catch (err) {
        console.error('Failed to fetch invitation:', err);
        if (err.response?.status === 404) {
          setError('This verification link is invalid or has expired.');
        } else if (err.response?.status === 410) {
          setError('This verification link has expired. Please request a new invitation.');
        } else {
          setError('Unable to load verification. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [token]);

  // Continue from existing progress - restore all data including images
  const handleContinueProgress = (continueFromProgress) => {
    if (continueFromProgress && existingProgress) {
      setCurrentStep(existingProgress.current_step);
      setFormData(prev => ({
        ...prev,
        full_name: existingProgress.full_name || prev.full_name,
        date_of_birth: existingProgress.date_of_birth || prev.date_of_birth,
        document_type: existingProgress.document_type || prev.document_type,
        document_number: existingProgress.document_number || prev.document_number,
        // Restore images from progress (cross-device sync)
        id_front_preview: existingProgress.id_front_base64 || prev.id_front_preview,
        id_back_preview: existingProgress.id_back_base64 || prev.id_back_preview,
        selfie_front_preview: existingProgress.selfie_front_base64 || prev.selfie_front_preview,
        selfie_side_preview: existingProgress.selfie_side_base64 || prev.selfie_side_preview
      }));
    }
    setShowContinueDialog(false);
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Allowed file types - HEIC/HEIF are not supported by OCR/image processing
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    const fileName = file.name.toLowerCase();
    const isHeic = fileName.endsWith('.heic') || fileName.endsWith('.heif');
    
    // Check file type
    if (isHeic || (!allowedTypes.includes(file.type) && !file.type.startsWith('image/'))) {
      toast.error('Unsupported file format. Please upload JPG, PNG, or WebP images. HEIC files are not supported.');
      e.target.value = ''; // Reset input
      return;
    }
    
    // Additional check for HEIC mime type variations
    if (file.type === 'image/heic' || file.type === 'image/heif') {
      toast.error('HEIC/HEIF files are not supported. Please convert to JPG or PNG first.');
      e.target.value = ''; // Reset input
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      e.target.value = ''; // Reset input
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({
        ...prev,
        [field]: file,
        [`${field}_preview`]: reader.result
      }));
      // Auto-save progress when file uploaded
      saveProgress(currentStep);
    };
    reader.readAsDataURL(file);
  };

  const removeFile = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: null,
      [`${field}_preview`]: null
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.full_name.trim()) {
          toast.error('Please enter your full name');
          return false;
        }
        return true;
      case 2:
        if (!formData.id_front && !formData.id_front_preview) {
          toast.error('Please upload the front of your ID');
          return false;
        }
        return true;
      case 3:
        if (!formData.selfie_front && !formData.selfie_front_preview) {
          toast.error('Please upload a front-facing selfie');
          return false;
        }
        if (!formData.selfie_side && !formData.selfie_side_preview) {
          toast.error('Please upload a side profile selfie');
          return false;
        }
        return true;
      case 4:
        if (!formData.consent_given) {
          toast.error('Please accept the consent to proceed');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = async () => {
    if (validateStep(currentStep)) {
      const newStep = Math.min(currentStep + 1, 4);
      setCurrentStep(newStep);
      await saveProgress(newStep);
    }
  };

  const prevStep = async () => {
    const newStep = Math.max(currentStep - 1, 1);
    setCurrentStep(newStep);
    await saveProgress(newStep);
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;

    try {
      setSubmitting(true);

      // Convert files to base64
      const toBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
      });

      // Handle both File objects and existing base64 previews
      const getBase64 = async (file, preview) => {
        if (file instanceof File) {
          return await toBase64(file);
        } else if (preview && preview.startsWith('data:')) {
          return preview.split(',')[1];
        }
        return null;
      };

      const submission = {
        full_name: formData.full_name,
        date_of_birth: formData.date_of_birth || null,
        document_type: formData.document_type,
        document_number: formData.document_number || null,
        id_front_base64: await getBase64(formData.id_front, formData.id_front_preview),
        id_back_base64: await getBase64(formData.id_back, formData.id_back_preview),
        selfie_front_base64: await getBase64(formData.selfie_front, formData.selfie_front_preview),
        selfie_side_base64: await getBase64(formData.selfie_side, formData.selfie_side_preview),
        consent_given: formData.consent_given
      };

      // Set a reasonable timeout - submission should be quick, analysis runs in background
      await axios.post(`${API_URL}/api/verify/${token}/submit`, submission, {
        timeout: 60000 // 60 second timeout
      });
      setSubmitted(true);
      toast.success('Verification submitted successfully!');
    } catch (err) {
      console.error('Failed to submit:', err);
      
      // Check if it's a timeout error
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        // Even if we timeout, the submission might have succeeded
        // Check the invitation status
        try {
          const statusCheck = await axios.get(`${API_URL}/api/verify/${token}`, { timeout: 10000 });
          if (statusCheck.data?.status === 'submitted') {
            setSubmitted(true);
            toast.success('Verification submitted successfully!');
            return;
          }
        } catch (checkErr) {
          console.error('Status check failed:', checkErr);
        }
        toast.error('Request timed out. Please check your internet connection and try again.');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to submit verification');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-slate-400" />
          <p className="mt-4 text-muted-foreground">Loading verification...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Verification Unavailable</h1>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-10 w-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Verification Submitted!</h1>
            <p className="text-muted-foreground mb-6">
              Thank you, {formData.full_name}. Your identity verification has been submitted and is now being reviewed.
            </p>
            <p className="text-sm text-muted-foreground">
              You will be notified once the review is complete.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const progress = (currentStep / 4) * 100;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="verify-flow">
      {/* Continue Dialog */}
      <Dialog open={showContinueDialog} onOpenChange={setShowContinueDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              Continue Where You Left Off?
            </DialogTitle>
            <DialogDescription>
              We found your previous progress on {existingProgress?.device_type === 'mobile' ? 'mobile' : 'desktop'}.
              Would you like to continue from step {existingProgress?.current_step}?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-slate-50 rounded-lg p-4 text-sm space-y-2">
              <p><strong>Name:</strong> {existingProgress?.full_name || '-'}</p>
              <p><strong>Progress:</strong> Step {existingProgress?.current_step} of 4</p>
              <p><strong>Documents:</strong> {existingProgress?.id_front_uploaded ? '✓ ID Front' : ''} {existingProgress?.selfie_uploaded ? '✓ Selfie' : ''}</p>
            </div>
          </div>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => handleContinueProgress(false)}>
              Start Fresh
            </Button>
            <Button onClick={() => handleContinueProgress(true)}>
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              Continue on Your Phone
            </DialogTitle>
            <DialogDescription>
              Scan this QR code with your phone's camera to continue the verification there. Your progress will be saved.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-6">
            <div className="bg-white p-4 rounded-xl shadow-inner border">
              <QRCodeSVG 
                value={verificationUrl}
                size={200}
                level="H"
                includeMargin={true}
              />
            </div>
            <p className="text-sm text-muted-foreground mt-4 text-center">
              Point your phone's camera at this code
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQRDialog(false)}>
              Continue on Desktop
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Session Taken Over Screen */}
      {/* Already Submitted Screen - shows first if submitted */}
      {isSubmitted && (
        <div className="max-w-md mx-auto mt-8">
          <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-slate-50">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-emerald-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">
                Verification Complete
              </h2>
              <p className="text-muted-foreground mb-4">
                Your identity verification has already been submitted. Our team will review your documents and notify you of the outcome.
              </p>
              <div className="bg-white rounded-lg p-4 border">
                <p className="text-sm text-slate-600">
                  You can close this window. No further action is required.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Session Taken Over Screen - only show if not submitted */}
      {sessionTakenOver && !isSubmitted && (
        <div className="max-w-md mx-auto mt-8">
          <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-slate-50">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-blue-100 rounded-full flex items-center justify-center">
                {activeDevice === 'mobile' ? (
                  <Smartphone className="h-8 w-8 text-blue-600" />
                ) : (
                  <Monitor className="h-8 w-8 text-blue-600" />
                )}
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">
                Session Transferred to {activeDevice === 'mobile' ? 'Mobile' : 'Desktop'}
              </h2>
              <p className="text-muted-foreground mb-6">
                {activeDevice === 'mobile' 
                  ? "You've opened this verification on your phone. Please continue there to complete the process."
                  : "This session is now active on desktop. Please continue there to complete the process."
                }
              </p>
              
              <div className="bg-white rounded-lg p-4 border mb-6">
                <div className="flex items-center justify-center gap-2 text-sm text-slate-600">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                  Active on: <span className="font-medium capitalize">{activeDevice}</span>
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground">
                Want to switch back to this device?
              </p>
              <Button 
                variant="outline" 
                className="mt-3"
                onClick={async () => {
                  await claimSession();
                }}
              >
                Continue Here Instead
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main content - only show if session is not taken over and not submitted */}
      {!sessionTakenOver && !isSubmitted && (
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img 
              src="/edomatch-logo-black.png" 
              alt="Edomatch" 
              className="h-10"
            />
          </div>
          <h1 className="text-2xl font-bold">Identity Verification</h1>
          <p className="text-muted-foreground mt-1">
            Complete the steps below to verify your identity for Edomatch
          </p>
        </div>

        {/* Device Switch Option - Show on Desktop for Steps 2 & 3 */}
        {!isMobile && (currentStep === 2 || currentStep === 3) && (
          <Card className="mb-6 border-2 border-blue-400 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-lg">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-500 rounded-xl shadow-md">
                    <Smartphone className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-blue-900 text-lg">Continue on your phone</p>
                    <p className="text-sm text-blue-700">Take photos directly with your phone camera for best results</p>
                  </div>
                </div>
                <Button 
                  size="lg" 
                  onClick={() => setShowQRDialog(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white shadow-md"
                >
                  <QrCode className="h-5 w-5 mr-2" />
                  Scan QR Code
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Show desktop option on mobile */}
        {isMobile && currentStep === 1 && (
          <Card className="mb-6 border-slate-200 bg-slate-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-200 rounded-lg">
                  <Monitor className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Using a phone?</p>
                  <p className="text-sm text-slate-600">Great! Your camera is ready for ID photos and selfies.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <Progress value={progress} className="h-2 flex-1" />
            {savingProgress && (
              <span className="text-xs text-muted-foreground ml-2 flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> Saving...
              </span>
            )}
          </div>
          <div className="flex justify-between">
            {STEPS.map((step) => (
              <div 
                key={step.id}
                className={cn(
                  "flex flex-col items-center gap-1",
                  currentStep >= step.id ? "text-slate-900" : "text-muted-foreground"
                )}
              >
                <div className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors",
                  currentStep > step.id ? "bg-emerald-100 border-emerald-500 text-emerald-600" :
                  currentStep === step.id ? "bg-slate-900 border-slate-900 text-white" :
                  "bg-white border-slate-200"
                )}>
                  {currentStep > step.id ? (
                    <CheckCircle2 className="h-5 w-5" />
                  ) : (
                    <step.icon className="h-5 w-5" />
                  )}
                </div>
                <span className="text-xs font-medium hidden sm:block">{step.title}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <Card>
          <CardContent className="p-6">
            {/* Step 1: Personal Info */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold mb-1">Personal Information</h2>
                  <p className="text-sm text-muted-foreground">
                    Please provide your details exactly as they appear on your ID document. This information will be verified against your document.
                  </p>
                </div>

                {/* Security Notice */}
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Shield className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-emerald-800">
                      <p className="font-medium">Secure & Private</p>
                      <p className="text-xs mt-0.5">Your data is encrypted in transit (TLS/HTTPS) and processed locally. Documents are analyzed on our secure servers without being shared with third parties.</p>
                    </div>
                  </div>
                </div>

                {/* Country-specific document recommendations */}
                {countryDocInfo && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium">Recommended for {countryDocInfo.country_name}</p>
                        <p className="text-xs mt-1">
                          Primary document: <strong>{countryDocInfo.common_document_names?.[countryDocInfo.primary_document] || countryDocInfo.primary_document}</strong>
                        </p>
                        {countryDocInfo.verification_notes?.length > 0 && (
                          <ul className="text-xs mt-1 list-disc list-inside">
                            {countryDocInfo.verification_notes.slice(0, 2).map((note, i) => (
                              <li key={i}>{note}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Full Name (as on document) *</Label>
                    <Input
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      placeholder="Enter your full name exactly as shown on ID"
                    />
                    <p className="text-xs text-muted-foreground">Must match your ID document exactly</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Date of Birth *</Label>
                    <Input
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">Will be verified against your document</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Document Type *</Label>
                    <Select 
                      value={formData.document_type}
                      onValueChange={(v) => setFormData({ ...formData, document_type: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(invitation?.accepted_document_types || DOCUMENT_TYPES.map(d => d.value)).map((type) => {
                          const doc = DOCUMENT_TYPES.find(d => d.value === type);
                          const countryName = countryDocInfo?.common_document_names?.[type];
                          return (
                            <SelectItem key={type} value={type}>
                              {countryName || doc?.label || type}
                              {countryDocInfo?.primary_document === type && ' (Recommended)'}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Document Number *</Label>
                    <Input
                      value={formData.document_number}
                      onChange={(e) => setFormData({ ...formData, document_number: e.target.value })}
                      placeholder="ID/Passport number as shown on document"
                    />
                    <p className="text-xs text-muted-foreground">Will be verified against your document</p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: ID Document */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold mb-1">Upload ID Document</h2>
                  <p className="text-sm text-muted-foreground">
                    {isMobile ? 'Take photos of your ID using your camera' : 'Take clear photos of the front and back of your ID'}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Front */}
                  <div className="space-y-2">
                    <Label>Front of ID *</Label>
                    {/* Camera input */}
                    <input
                      ref={fileInputFrontRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                      capture={isMobile ? "environment" : undefined}
                      className="hidden"
                      onChange={(e) => handleFileChange(e, 'id_front')}
                    />
                    {/* Gallery input (no capture - opens file picker) */}
                    <input
                      ref={galleryInputFrontRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                      className="hidden"
                      onChange={(e) => handleFileChange(e, 'id_front')}
                    />
                    {formData.id_front_preview ? (
                      <div className="relative">
                        <img 
                          src={formData.id_front_preview} 
                          alt="ID Front" 
                          className="w-full h-40 object-cover rounded-lg border"
                        />
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2 h-8 w-8"
                          onClick={() => removeFile('id_front')}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : isMobile ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => fileInputFrontRef.current?.click()}
                          className="flex-1 h-40 border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 hover:border-slate-400 transition-colors"
                        >
                          <Camera className="h-8 w-8 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Take photo</span>
                        </button>
                        <button
                          onClick={() => galleryInputFrontRef.current?.click()}
                          className="flex-1 h-40 border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 hover:border-slate-400 transition-colors"
                        >
                          <Upload className="h-8 w-8 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Upload file</span>
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => fileInputFrontRef.current?.click()}
                        className="w-full h-40 border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 hover:border-slate-400 transition-colors"
                      >
                        <Upload className="h-8 w-8 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Click to upload</span>
                      </button>
                    )}
                  </div>

                  {/* Back */}
                  <div className="space-y-2">
                    <Label>Back of ID (Optional)</Label>
                    {/* Camera input */}
                    <input
                      ref={fileInputBackRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                      capture={isMobile ? "environment" : undefined}
                      className="hidden"
                      onChange={(e) => handleFileChange(e, 'id_back')}
                    />
                    {/* Gallery input (no capture - opens file picker) */}
                    <input
                      ref={galleryInputBackRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                      className="hidden"
                      onChange={(e) => handleFileChange(e, 'id_back')}
                    />
                    {formData.id_back_preview ? (
                      <div className="relative">
                        <img 
                          src={formData.id_back_preview} 
                          alt="ID Back" 
                          className="w-full h-40 object-cover rounded-lg border"
                        />
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2 h-8 w-8"
                          onClick={() => removeFile('id_back')}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : isMobile ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => fileInputBackRef.current?.click()}
                          className="flex-1 h-40 border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 hover:border-slate-400 transition-colors"
                        >
                          <Camera className="h-8 w-8 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Take photo</span>
                        </button>
                        <button
                          onClick={() => galleryInputBackRef.current?.click()}
                          className="flex-1 h-40 border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 hover:border-slate-400 transition-colors"
                        >
                          <Upload className="h-8 w-8 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Upload file</span>
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => fileInputBackRef.current?.click()}
                        className="w-full h-40 border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 hover:border-slate-400 transition-colors"
                      >
                        <Upload className="h-8 w-8 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Click to upload</span>
                      </button>
                    )}
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                  <p className="font-medium">Tips for a good photo:</p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>Place document on a flat, dark surface</li>
                    <li>Ensure all corners are visible</li>
                    <li>Avoid glare and shadows</li>
                    <li>Make sure text is readable</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Step 3: Selfies */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold mb-1">Take Two Selfies</h2>
                  <p className="text-sm text-muted-foreground">
                    We need a front-facing photo and a side profile to verify your identity
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Front Face Selfie */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <span className="w-6 h-6 bg-slate-900 text-white rounded-full text-xs flex items-center justify-center">1</span>
                      Front Face Selfie *
                    </Label>
                    {/* Camera input */}
                    <input
                      ref={fileInputSelfieFrontRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                      capture={isMobile ? "user" : undefined}
                      className="hidden"
                      onChange={(e) => handleFileChange(e, 'selfie_front')}
                    />
                    {/* Gallery input (no capture - opens file picker) */}
                    <input
                      ref={galleryInputSelfieFrontRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                      className="hidden"
                      onChange={(e) => handleFileChange(e, 'selfie_front')}
                    />
                    {formData.selfie_front_preview ? (
                      <div className="relative">
                        <img 
                          src={formData.selfie_front_preview} 
                          alt="Front Selfie" 
                          className="w-full h-48 object-cover rounded-lg border"
                        />
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2 h-8 w-8"
                          onClick={() => removeFile('selfie_front')}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                        <div className="absolute bottom-2 left-2 bg-emerald-500 text-white text-xs px-2 py-1 rounded">
                          Front Face ✓
                        </div>
                      </div>
                    ) : isMobile ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => fileInputSelfieFrontRef.current?.click()}
                          className="flex-1 h-48 border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 hover:border-slate-400 transition-colors"
                        >
                          <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                            <Camera className="h-6 w-6 text-slate-400" />
                          </div>
                          <span className="text-sm text-muted-foreground">Take selfie</span>
                        </button>
                        <button
                          onClick={() => galleryInputSelfieFrontRef.current?.click()}
                          className="flex-1 h-48 border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 hover:border-slate-400 transition-colors"
                        >
                          <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                            <Upload className="h-6 w-6 text-slate-400" />
                          </div>
                          <span className="text-sm text-muted-foreground">Upload file</span>
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => fileInputSelfieFrontRef.current?.click()}
                        className="w-full h-48 border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 hover:border-slate-400 transition-colors"
                      >
                        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                          <Camera className="h-8 w-8 text-slate-400" />
                        </div>
                        <span className="text-sm text-muted-foreground">Look straight at camera</span>
                      </button>
                    )}
                  </div>

                  {/* Side Profile Selfie */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <span className="w-6 h-6 bg-slate-900 text-white rounded-full text-xs flex items-center justify-center">2</span>
                      Side Profile Selfie *
                    </Label>
                    {/* Camera input */}
                    <input
                      ref={fileInputSelfieSideRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                      capture={isMobile ? "user" : undefined}
                      className="hidden"
                      onChange={(e) => handleFileChange(e, 'selfie_side')}
                    />
                    {/* Gallery input (no capture - opens file picker) */}
                    <input
                      ref={galleryInputSelfieSideRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                      className="hidden"
                      onChange={(e) => handleFileChange(e, 'selfie_side')}
                    />
                    {formData.selfie_side_preview ? (
                      <div className="relative">
                        <img 
                          src={formData.selfie_side_preview} 
                          alt="Side Selfie" 
                          className="w-full h-48 object-cover rounded-lg border"
                        />
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2 h-8 w-8"
                          onClick={() => removeFile('selfie_side')}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                        <div className="absolute bottom-2 left-2 bg-emerald-500 text-white text-xs px-2 py-1 rounded">
                          Side Profile ✓
                        </div>
                      </div>
                    ) : isMobile ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => fileInputSelfieSideRef.current?.click()}
                          className="flex-1 h-48 border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 hover:border-slate-400 transition-colors"
                        >
                          <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center relative">
                            <Camera className="h-6 w-6 text-slate-400" />
                            <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-0 h-0 border-t-3 border-t-transparent border-b-3 border-b-transparent border-l-4 border-l-slate-300"></div>
                          </div>
                          <span className="text-sm text-muted-foreground">Take photo</span>
                        </button>
                        <button
                          onClick={() => galleryInputSelfieSideRef.current?.click()}
                          className="flex-1 h-48 border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 hover:border-slate-400 transition-colors"
                        >
                          <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                            <Upload className="h-6 w-6 text-slate-400" />
                          </div>
                          <span className="text-sm text-muted-foreground">Upload file</span>
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => fileInputSelfieSideRef.current?.click()}
                        className="w-full h-48 border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 hover:border-slate-400 transition-colors"
                      >
                        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center relative">
                          <Camera className="h-8 w-8 text-slate-400" />
                          <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-0 h-0 border-t-4 border-t-transparent border-b-4 border-b-transparent border-l-6 border-l-slate-300"></div>
                        </div>
                        <span className="text-sm text-muted-foreground">Turn head 45° to the side</span>
                      </button>
                    )}
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                  <p className="font-medium">Why two selfies?</p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li><strong>Front face:</strong> We compare this with your ID photo</li>
                    <li><strong>Side profile:</strong> Confirms you're a real person (not a flat image)</li>
                    <li>Ensure good lighting on your face</li>
                    <li>Remove glasses and hats if possible</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Step 4: Review */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold mb-1">Review & Submit</h2>
                  <p className="text-sm text-muted-foreground">
                    Please review your information before submitting
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Name</span>
                      <span className="font-medium">{formData.full_name}</span>
                    </div>
                    {formData.date_of_birth && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Date of Birth</span>
                        <span className="font-medium">{formData.date_of_birth}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Document Type</span>
                      <span className="font-medium capitalize">{formData.document_type.replace('_', ' ')}</span>
                    </div>
                    {formData.document_number && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Document Number</span>
                        <span className="font-medium">{formData.document_number}</span>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {formData.id_front_preview && (
                      <div className="space-y-1">
                        <img src={formData.id_front_preview} alt="ID Front" className="w-full h-20 object-cover rounded border" />
                        <p className="text-xs text-center text-muted-foreground">ID Front</p>
                      </div>
                    )}
                    {formData.id_back_preview && (
                      <div className="space-y-1">
                        <img src={formData.id_back_preview} alt="ID Back" className="w-full h-20 object-cover rounded border" />
                        <p className="text-xs text-center text-muted-foreground">ID Back</p>
                      </div>
                    )}
                    {formData.selfie_front_preview && (
                      <div className="space-y-1">
                        <img src={formData.selfie_front_preview} alt="Front Selfie" className="w-full h-20 object-cover rounded border" />
                        <p className="text-xs text-center text-muted-foreground">Front Selfie</p>
                      </div>
                    )}
                    {formData.selfie_side_preview && (
                      <div className="space-y-1">
                        <img src={formData.selfie_side_preview} alt="Side Selfie" className="w-full h-20 object-cover rounded border" />
                        <p className="text-xs text-center text-muted-foreground">Side Selfie</p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-start gap-3 p-4 border rounded-lg">
                    <Checkbox 
                      id="consent"
                      checked={formData.consent_given}
                      onCheckedChange={(checked) => setFormData({ ...formData, consent_given: checked })}
                    />
                    <label htmlFor="consent" className="text-sm leading-relaxed">
                      I confirm that the information provided is accurate and I consent to Edomatch processing my personal data for identity verification purposes in accordance with the privacy policy.
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 1}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>

              {currentStep < 4 ? (
                <Button onClick={nextStep}>
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={submitting}>
                  {submitting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  Submit Verification
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          Your data is encrypted and securely processed. For support, contact verify@edomatch.com
        </p>
      </div>
      )}
    </div>
  );
}

export default VerifyFlow;
